import { NavLink, Link, useNavigate } from "react-router-dom";
import { ShoppingBag, User, LogOut, Menu, X } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

export default function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const linkClass = ({ isActive }) =>
    `text-sm tracking-wide link-underline ${isActive ? "text-[#8C3A3A] font-semibold" : "text-[#2D2A26]"}`;

  return (
    <header className="glass-header sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2">
          <span className="font-display text-2xl md:text-3xl font-semibold tracking-tight">Eko</span>
          <span className="overline hidden sm:inline">Cakes & Bakes</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/" end className={linkClass} data-testid="nav-home">Home</NavLink>
          <NavLink to="/shop/cakes" className={linkClass} data-testid="nav-cakes">Cakes</NavLink>
          <NavLink to="/shop/flowers" className={linkClass} data-testid="nav-flowers">Flowers</NavLink>
          <NavLink to="/shop/hampers" className={linkClass} data-testid="nav-hampers">Hampers</NavLink>
          <NavLink to="/shop/mugs" className={linkClass} data-testid="nav-mugs">Mugs</NavLink>
          <NavLink to="/shop/combos" className={linkClass} data-testid="nav-combos">Combos</NavLink>
          <NavLink to="/occasions/birthday" className={linkClass} data-testid="nav-occasions">Occasions</NavLink>
        </nav>

        <div className="flex items-center gap-3 md:gap-5">
          {user ? (
            <div className="hidden md:flex items-center gap-3">
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin" className="text-sm font-semibold text-[#8C3A3A]">Admin</Link>
              )}
              <Link to="/orders" data-testid="nav-orders" className="text-sm link-underline">Orders</Link>
              <span className="text-sm text-[#6C665F]">Hi, {user.name.split(" ")[0]}</span>
              <button data-testid="btn-logout" onClick={() => { logout(); navigate("/"); }} className="text-[#6C665F] hover:text-[#8C3A3A]">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <Link to="/login" data-testid="nav-login" className="hidden md:inline-flex items-center gap-2 text-sm">
              <User className="w-4 h-4" /> Sign in
            </Link>
          )}
          <Link to="/cart" data-testid="nav-cart" className="relative">
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 bg-[#8C3A3A] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {count}
              </span>
            )}
          </Link>
          <button className="md:hidden" data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)}>
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#E5DFD3] bg-white/95 px-6 py-4 flex flex-col gap-3">
          <NavLink to="/" end onClick={() => setOpen(false)} className={linkClass}>Home</NavLink>
          <NavLink to="/shop/cakes" onClick={() => setOpen(false)} className={linkClass}>Cakes</NavLink>
          <NavLink to="/shop/flowers" onClick={() => setOpen(false)} className={linkClass}>Flowers</NavLink>
          <NavLink to="/shop/hampers" onClick={() => setOpen(false)} className={linkClass}>Hampers</NavLink>
          <NavLink to="/shop/mugs" onClick={() => setOpen(false)} className={linkClass}>Mugs</NavLink>
          <NavLink to="/shop/combos" onClick={() => setOpen(false)} className={linkClass}>Combos</NavLink>
          <NavLink to="/occasions/birthday" onClick={() => setOpen(false)} className={linkClass}>Occasions</NavLink>
          {user ? (
            <>
              {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)}>Admin</Link>}
              <Link to="/orders" onClick={() => setOpen(false)}>My Orders</Link>
              <button onClick={() => { logout(); setOpen(false); navigate("/"); }}>Logout</button>
            </>
          ) : (
            <Link to="/login" onClick={() => setOpen(false)}>Sign in</Link>
          )}
        </div>
      )}
    </header>
  );
}
